import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import FileUpload from "../shared/FileUpload";
import SubmitFeedback from "../shared/SubmitFeedback";
import apiService from "../../services/api-service";
import { useAuth } from "../../contexts/AuthContext";

interface InvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const InvoiceDialog: React.FC<InvoiceDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [formData, setFormData] = useState({
    remarks: "",
    attachment: undefined as File | undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(undefined);
    setSuccess(undefined);

    try {
      if (!formData.attachment) {
        throw new Error(t("error.fileRequired"));
        return;
      }

      // Step 1: Create invoice request
      const createResponse = await apiService.createInvoiceRequest(
        user?.username || "",
      );
      const uuid = createResponse.uuid;

      // Step 2: Upload attachment
      await apiService.uploadInvoiceAttachment(
        user?.username || "",
        uuid,
        formData.attachment,
        formData.remarks,
      );

      // Step 3: Refresh the list
      await apiService.getInvoiceList(user?.username || "");

      setSuccess(t("common.success"));
      onSubmit();
      setTimeout(() => {
        onClose();
        setFormData({
          remarks: "",
          attachment: undefined,
        });
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (_event: object, reason: string) => {
    if (reason !== "backdropClick") {
      handleButtonClose();
    }
  };

  const handleButtonClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={loading}
        slotProps={{
          backdrop: {
            onClick: (e) => e.stopPropagation(),
          },
        }}
      >
        <DialogTitle>{t("invoice.new")}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label={t("invoice.remarks")}
              margin="normal"
              value={formData.remarks}
              onChange={(e) =>
                setFormData({ ...formData, remarks: e.target.value })
              }
              multiline
              rows={4}
              disabled={loading}
            />
            <Box sx={{ mt: 2 }}>
              <FileUpload
                onFileSelect={(files) =>
                  setFormData({ ...formData, attachment: files?.[0] })
                }
                label={t("invoice.attachment")}
                disabled={loading}
                selectedFiles={formData.attachment ? [formData.attachment] : []}
                multiple={false}
                maxFileSize={5 * 1024 * 1024} // 5MB
                acceptedFileTypes={[".pdf", ".zip", ".jpg", ".jpeg", ".png"]}
                required={true}
                showRestrictions={false}
              />
              {!formData.attachment && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 1 }}
                >
                  {t("invoice.noAttachment")}
                </Typography>
              )}
              <Typography variant="caption" color="text.primary" sx={{ mt: 1 }}>
                <Trans i18nKey="invoice.formatRequirement" />
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleButtonClose} disabled={loading}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !formData.attachment}
            >
              {t("common.submit")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <SubmitFeedback
        loading={loading}
        success={success}
        error={error}
        onCloseError={() => {
          setSuccess(undefined);
          setError(undefined);
        }}
        onCloseSuccess={() => {
          setSuccess(undefined);
          setError(undefined);
        }}
      />
    </>
  );
};

export default InvoiceDialog;
